/**
 * The one hook the entry screen uses for BC on-hand (Cold Link only; see
 * `onHandView.ts`). Kept out of `Entry.tsx` so that file still contains no
 * on-hand vocabulary at all — `test_the_entry_screen_never_reads_an_endpoint_that_knows_on_hand`
 * scans it, and at the five blind-count areas this hook is inert.
 */
import { useSessionOnHand } from "../api/queries";
import type { CountArea, CountListItem } from "../api/types";
import { onHandRowView, type OnHandRowView } from "./onHandView";

export function useEntryOnHand(area: CountArea | undefined, areaId: string, sessionId: string) {
  const flagged = area?.show_on_hand_during_entry === true;
  const query = useSessionOnHand(areaId, sessionId, flagged);
  const byItem = new Map(
    (query.data?.items ?? []).map((row) => [row.item_no.trim().toUpperCase(), row] as const),
  );
  return {
    flagged,
    asOf: query.data?.as_of ?? null,
    byItem,
    loading: flagged && query.isFetching,
    error: flagged && query.error ? (query.error as Error).message : null,
    /** Null at any unflagged area, and for an item the read did not return. */
    viewFor(
      item: CountListItem,
      unit: string | null,
      typedQty: string | null | undefined,
    ): OnHandRowView | null {
      const row = byItem.get(item.item_no.trim().toUpperCase());
      if (!flagged || !row) return null;
      return onHandRowView(item, row.on_hand_base_qty, row.base_unit_of_measure, unit, typedQty);
    },
    refresh: () => void query.refetch(),
  };
}
